import { Link } from 'react-router-dom';
import { COMPANY, SERVICE_AREAS } from '../../mocks/siteData';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface ServiceAreasSectionProps {
  className?: string;
  headingText?: string;
  bodyText?: string;
}

export default function ServiceAreasSection({
  className = '',
  headingText = 'Serving Homeowners Throughout the Bay City Area',
  bodyText = 'Bay City Remodeling provides kitchen, bathroom, and full-home remodeling services across Brandon, FL and surrounding communities — with deep knowledge of local homes, styles, and property expectations.',
}: ServiceAreasSectionProps) {
  const { ref, visible } = useScrollAnimation();

  return (
    <section className={`bg-white py-32 px-6 lg:px-16 xl:px-24 ${className}`}>
      <div className="max-w-6xl mx-auto">

        {/* Heading */}
        <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-accent text-xs font-semibold tracking-[0.2em] uppercase mb-4">Service Areas</p>
            <h2
              className="font-serif text-4xl lg:text-[52px] text-stone-900 leading-tight max-w-2xl"
              style={{ letterSpacing: '-0.02em' }}
            >
              {headingText}
            </h2>
          </div>
          <Link
            to="/near-me"
            className="inline-flex items-center gap-2 text-accent hover:text-accent-dark font-semibold text-sm whitespace-nowrap transition-colors group"
          >
            View All Areas
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs transition-transform group-hover:translate-x-1" />
            </div>
          </Link>
        </div>

        {/* Map + Areas */}
        <div
          ref={ref}
          className={`grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Google Map */}
          <div className="relative rounded-2xl overflow-hidden h-[500px] bg-stone-100">
            <iframe
              src={COMPANY.mapEmbed}
              width="100%"
              height="100%"
              loading="lazy"
              allowFullScreen
              title="Bay City Remodeling Service Area Map — Brandon FL"
              className="w-full h-full border-0"
            />
            {/* Map overlay tag */}
            <div className="absolute top-4 left-4 bg-white border border-stone-100 rounded-xl px-4 py-2.5 flex items-center gap-2">
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-map-pin-fill text-accent text-xs" />
              </div>
              <span className="text-stone-800 text-xs font-semibold">Brandon, FL &amp; Surrounding Areas</span>
            </div>
          </div>

          {/* Area list */}
          <div className="flex flex-col">
            <p className="text-stone-500 text-sm leading-relaxed mb-7">{bodyText}</p>

            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mb-7">
              {SERVICE_AREAS.slice(0, 18).map((area) => (
                <Link
                  key={area.slug}
                  to={`/near-me/${area.slug}`}
                  className="flex items-center gap-2 text-stone-500 hover:text-accent text-sm py-1.5 transition-colors group"
                >
                  <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-stone-300 group-hover:text-accent text-xs transition-colors" />
                  </div>
                  <span className="whitespace-nowrap text-xs">{area.name}</span>
                </Link>
              ))}
            </div>

            <div className="border-t border-stone-100 pt-6 flex flex-col gap-3">
              <Link
                to="/about"
                className="inline-flex items-center justify-center gap-3 bg-stone-950 hover:bg-stone-800 text-white font-semibold px-6 py-4 rounded-lg transition-colors whitespace-nowrap group text-sm"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-smile-line text-accent" />
                </div>
                Request an In-Home Consultation
              </Link>
              <a
                href={`tel:${COMPANY.phoneFull}`}
                className="inline-flex items-center justify-center gap-2 border border-stone-200 hover:border-accent/40 text-stone-600 hover:text-accent px-6 py-3.5 rounded-lg transition-colors whitespace-nowrap text-sm font-medium"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line text-accent text-xs" />
                </div>
                Call {COMPANY.phone}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
